<template>
  <div id="info-dashboard-container">
    <div id="info-dashboard-subheader">
      <div v-for="(item, index) in subHeaderItems" :key="index">
        <p v-if="!item.isBadge" class="subheader-item">{{ item.text }}</p>
        <BadgeSquare v-else :badgeText="item.text" class="subheader-item" size="small" />
      </div>
    </div>
    <div id="info-dashboard-content">
      <FeatureChildren
        feature="home/info/info/general"
        :contentData="report?.groupData"
        :contentTitles="report?.groupTitles"
        :measurements="report?.measurements"
        :currentEntity="currentEntity"
        :isLoading="(currentLoading || isLoading) ?? false"
        :lineSummaryData="lineSummaryData"
        :indoorData="roomReport?.measurements.value ?? []"
        :infoData="infoData"
        :poiSummaryData="poiSummaryData"
        :zoneSummaryData="zoneSummaryData"
        :overviewData="report?.summaryReport"
        :poiMetaInfo="poiReport?.metaInfo.value ?? []"
      ></FeatureChildren>
      <div id="info-dashboard-footer">
        <IconInfoCircle class="info" />
        <p>{{ t('info__data-displayed') }}</p>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import FeatureChildren from '@/components/FeatureChildren.vue'
import useReportPoi from '@/hooks/reports/useReportPoi'
import { storeToRefs } from 'pinia'
import { useSelectionStore } from '@/pinia/selection'
import { useI18n } from 'vue-i18n'
import { Feature } from '@/pinia/feature'
import { computed } from 'vue'
import {
  generateEntityInformation,
  generateLineInformation,
  generatePoiInformation,
  generateZoneInformation,
  InfoData
} from '@/utils/entityInformation'
import BadgeSquare from '@/design-system/atoms/BadgeSquare.vue'
import IconInfoCircle from '@/design-system/assets/icons/IconInfoCircle.vue'
import { subHeaderItem, useEntitySubHeaders } from '@/utils/entitySubHeaders'
import useReportRoom from '@/hooks/reports/useReportRoom'
import { useReport } from '@/hooks/reports/useReport'

defineProps<{
  children: Feature[]
  title?: string
}>()

const { t } = useI18n()
const entityHeaders = useEntitySubHeaders()
const selectionStore = useSelectionStore()
const { clickedEntityType, currentEntity, currentLoading } = storeToRefs(selectionStore)
const poiReport = useReportPoi()
const roomReport = useReportRoom()
const { report, isLoading } = storeToRefs(useReport())

const lineSummaryData = computed(() => {
  return report.value?.lineSummaryData ?? []
})
const poiSummaryData = computed(() => {
  return report.value?.poiSummaryData ?? []
})
const subHeaderItems = computed<subHeaderItem[]>(() => {
  if (clickedEntityType.value === 'CONSTRUCTION')
    return entityHeaders.generateConstructionSubHeaders(currentEntity.value)
  if (clickedEntityType.value === 'LEVEL')
    return entityHeaders.generateLevelSubHeaders(currentEntity.value)
  if (clickedEntityType.value === 'SPACE')
    return entityHeaders.generateSpaceSubHeaders(currentEntity.value)
  if (clickedEntityType.value === 'POI') return entityHeaders.generatePoiSubHeaders()
  if (clickedEntityType.value === 'LINE') return entityHeaders.generateLineSubHeaders()
  if (clickedEntityType.value === 'ZONE') return entityHeaders.generateZoneSubHeaders()
  return []
})
const zoneSummaryData = computed(() => {
  return report.value?.zoneSummaryData ?? []
})

const infoData = computed<InfoData[]>(() => {
  let data: InfoData[] = []
  if (
    clickedEntityType.value !== 'POI' &&
    clickedEntityType.value !== 'LINE' &&
    clickedEntityType.value !== 'ZONE' &&
    clickedEntityType.value !== 'REGION' &&
    currentEntity.value
  ) {
    data = generateEntityInformation(currentEntity.value)
    return data
  } else if (clickedEntityType.value === 'POI') {
    data = generatePoiInformation(poiReport)
    return data
  } else if (clickedEntityType.value === 'LINE') {
    data = generateLineInformation()
    return data
  } else if (clickedEntityType.value === 'ZONE') {
    data = generateZoneInformation()
    return data
  }

  return []
})
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';

#info-dashboard-container {
  display: flex;
  flex-direction: column;
  height: auto;
  width: 100%;

  @media screen and (max-width: 1024px) {
    height: 100%;
    max-height: calc(100vh - #{variables.$layout-top-bar-height} - 56px);
  }
}

#info-dashboard-subheader {
  @include font-mixins.desktop-regular-label;
  background-color: #fff;
  border-bottom: 1px solid variables.$color-greyscale-line-grey;
  color: variables.$color-greyscale-secondary;
  display: flex;
  flex-direction: column;
  height: fit-content;
  width: 100%;
  padding: 0 24px 16px 24px;

  .subheader-item {
    margin-top: 4px;
  }

  & p {
    @include font-mixins.desktop-regular-label;
    color: variables.$color-greyscale-secondary;
    margin: 0;
    padding: 0;
  }
}

#info-dashboard-content {
  background-color: #fff;
  border-bottom-right-radius: variables.$border-radius-small;
  border-bottom-left-radius: variables.$border-radius-small;
  display: flex;
  flex-flow: column;
  gap: 8px;
  height: fit-content;
  overflow-x: hidden;
  overflow-y: auto;
  width: 100%;
  padding: 16px;
  scrollbar-width: none; // Firefox

  &::-webkit-scrollbar {
    display: none; // Chrome and Safari
  }

  &.mobile {
    border-radius: 0;
    bottom: 0;
    height: 100%;
    left: 0;
    position: fixed;
    right: 0;
    top: calc(#{variables.$layout-top-bar-height} + 56px);
    max-height: calc(100vh - #{variables.$layout-top-bar-height} - 56px);
    width: 100%;
  }
}

#info-dashboard-footer {
  align-items: center;
  display: flex;
  gap: 4px;
  padding: 24px 0 8px 0;

  .info {
    color: variables.$color-greyscale-secondary;
    display: inline-flex;
    height: 16px;
    min-width: 16px;
    max-width: 16px;
  }

  & p {
    @include font-mixins.desktop-regular-tiny;
    color: variables.$color-greyscale-secondary;
    margin: 0;
    padding: 0;
  }
}
</style>
